import { z } from 'zod';

/**
 * Device-capability telemetry payload (CA Stage-1, CA-004;
 * FEATURES.deviceTelemetry). One capture per user, latest-wins, stored as the
 * `deviceCapabilities` attribute on the user's profile item (CA-001) — so GDPR
 * export/delete cover it automatically. Consent: preferences.analyticsOptIn
 * (CA-003); no consent → no capture, client- AND server-enforced.
 *
 * Every probed field is possibly-null BY DESIGN — "API absent" is itself a
 * tiering signal. GPU identity is ONLY the WebGPU adapter's vendor +
 * architecture strings; no renderer strings, no fingerprinting machinery.
 *
 * HOW TO DISABLE / REMOVE: set FEATURES.deviceTelemetry=false (routes 404, the
 * app never probes). To remove: delete this file + device-insights.ts, the
 * `deviceCapabilities` field on UserProfileSchema, the two API_ROUTES entries,
 * infra/lambda/api/device-telemetry.ts (+ its index.ts/admin.ts dispatch
 * lines), apps/app/src/lib/device-telemetry.ts (+ its auth.ts call sites), the
 * admin insights section, and scripts/verify-device-telemetry*.ts. Existing
 * user rows keep a dead attribute until account deletion.
 */

/**
 * WebGPU availability as a 3-state tier signal:
 * - 'adapter'    — `navigator.gpu` present AND `requestAdapter()` granted one
 * - 'no-adapter' — API present but no adapter (blocklisted GPU, software
 *                  rendering, or the request threw) — a distinct tier from:
 * - 'absent'     — no `navigator.gpu` at all (Safari/Firefox stable, old Chrome)
 */
export const WebGpuSupport = z.enum(['adapter', 'no-adapter', 'absent']);
export type WebGpuSupport = z.infer<typeof WebGpuSupport>;

/** What the client POSTs to /me/device-telemetry (capturedAt is stamped server-side, CA-004). */
export const DeviceCapabilitiesInput = z.object({
  webgpu: WebGpuSupport,
  gpuVendor: z.string().max(120).nullable(),
  gpuArchitecture: z.string().max(120).nullable(),
  /** navigator.deviceMemory in GiB — Chrome-only, quantized 0.25–8. Null = API absent. */
  deviceMemory: z.number().positive().nullable(),
  hardwareConcurrency: z.number().int().positive().nullable(),
});
export type DeviceCapabilitiesInput = z.infer<typeof DeviceCapabilitiesInput>;

/** What is stored on the user profile item. */
export const DeviceCapabilitiesSchema = DeviceCapabilitiesInput.extend({
  capturedAt: z.string().datetime(),
});
export type DeviceCapabilities = z.infer<typeof DeviceCapabilitiesSchema>;

/** Raw, untrusted values straight off the browser APIs (everything may be missing). */
export interface DeviceCapabilityProbe {
  /** `'gpu' in navigator` */
  webgpuApi: boolean;
  /** `requestAdapter()` outcome — null when the API is absent or the probe didn't run. */
  adapterOk: boolean | null;
  gpuVendor?: unknown;
  gpuArchitecture?: unknown;
  deviceMemory?: unknown;
  hardwareConcurrency?: unknown;
}

const MAX_GPU_STRING = 120;

function cleanString(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t ? t.slice(0, MAX_GPU_STRING) : null;
}

function cleanPositiveNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * Pure normalization of a raw probe into the POST payload: coerce every
 * malformed/absent value to null (nulls are data), clamp strings, and only
 * keep GPU identity when an adapter was actually granted (info from anything
 * else is incoherent). Never throws.
 */
export function normalizeDeviceCapabilities(probe: DeviceCapabilityProbe): DeviceCapabilitiesInput {
  const webgpu: WebGpuSupport = !probe.webgpuApi
    ? 'absent'
    : probe.adapterOk === true
      ? 'adapter'
      : 'no-adapter';
  const cores = cleanPositiveNumber(probe.hardwareConcurrency);
  return {
    webgpu,
    gpuVendor: webgpu === 'adapter' ? cleanString(probe.gpuVendor) : null,
    gpuArchitecture: webgpu === 'adapter' ? cleanString(probe.gpuArchitecture) : null,
    deviceMemory: cleanPositiveNumber(probe.deviceMemory),
    hardwareConcurrency: cores === null ? null : Math.round(cores),
  };
}
